/**
 * OAuth Handler Hook - Simplified
 * Handles OAuth authentication flow for MCP servers using polling approach
 */
import { useToast } from '../../hooks/use-toast';
import { apiClient } from '../../services/api';
import { mcpBridgeService } from '../../services/mcp-bridge';

interface OAuthHandlerProps {
  user: any;
  mcpServerId: string;
  serverName: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  setConnecting: (connecting: boolean) => void;
  checkConnectionStatus: () => void;
}

export const useOAuthHandler = ({
  user,
  mcpServerId,
  serverName,
  onSuccess,
  onError,
  setConnecting,
  checkConnectionStatus
}: OAuthHandlerProps) => {
  const { toast } = useToast();

  const saveAuthCredentials = async (credentials: any) => {
    if (!user?.id) return;
    
    try {
      const response = await apiClient.post('/api/mcp/auth/store', {
        tenant_id: user.id,
        mcp_server_id: mcpServerId,
        credentials: credentials
      });
      
      if (response.data?.success) {
        toast({
          title: 'Authentication Saved',
          description: 'Your credentials have been securely stored'
        });
        await checkConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to save auth credentials:', error);
      toast({
        title: 'Failed to Save Credentials',
        description: 'Please try authenticating again',
        variant: 'destructive'
      });
    }
  };

  const handleOAuth = async (authUrl: string) => {
    try {
      // Generate a unique state for this OAuth flow
      const oauthState = `${user.id}_${mcpServerId}_${Date.now()}`;
      sessionStorage.setItem('oauth_state', oauthState);
      sessionStorage.setItem('oauth_mcp_server_id', mcpServerId);
      
      // Use Composio's OAuth URL directly - no modifications needed
      const url = new URL(authUrl);
      if (!url.searchParams.has('state')) {
        url.searchParams.set('state', oauthState);
      }
      
      // Open OAuth window
      const authWindow = window.open(url.toString(), 'oauth', 'width=600,height=700,scrollbars=yes,resizable=yes');
      
      if (!authWindow) {
        throw new Error('Failed to open OAuth popup window. Please check if popups are blocked.');
      }
      
      toast({
        title: 'Authenticating with ' + serverName, 
        description: 'Please complete authentication in the popup window. We will automatically detect completion.'
      });
      
      setConnecting(true);
      
      // Simple polling approach - let Composio handle the redirect
      let pollAttempts = 0;
      const maxPollAttempts = 30; // 5 minutes of polling (30 * 10 seconds)
      
      const pollForCompletion = async () => {
        try {
          pollAttempts++;
          console.log(`Polling for OAuth completion (attempt ${pollAttempts}/${maxPollAttempts})`);
          
          // Check if user closed the window (handle COOP gracefully)
          let windowClosed = false;
          try {
            windowClosed = authWindow.closed;
          } catch (e) {
            // Cross-Origin-Opener-Policy blocks window.closed - that's okay
            console.log('Cannot check window.closed due to COOP policy - continuing to poll backend');
          }
          
          if (windowClosed) {
            console.log('OAuth window was closed by user');
            
            // Give it a few more attempts in case they just finished
            if (pollAttempts >= 5) {
              toast({
                title: 'Authentication Cancelled',
                description: 'OAuth window was closed before completion',
                variant: 'destructive'
              });
              onError('OAuth window closed');
              setConnecting(false);
              return;
            }
          }
          
          // Check backend for completion
          const status = await mcpBridgeService.pollOAuthStatus(user.id, mcpServerId);
          
          if (status.completed && status.connected) {
            toast({
              title: 'Connected Successfully!',
              description: `Successfully connected to ${serverName}`
            });
            
            // Close OAuth window if still open
            try {
              authWindow?.close();
            } catch (e) {
              console.log('OAuth window already closed');
            }
            
            onSuccess();
            setConnecting(false);
            return;
          }
          
          // Continue polling if not completed yet
          if (pollAttempts < maxPollAttempts) {
            setTimeout(pollForCompletion, 10000); // Poll every 10 seconds
          } else {
            throw new Error('OAuth authentication timed out after 5 minutes');
          }
        } catch (error) {
          console.error('Error during OAuth polling:', error);
          
          // Retry on error, unless we've exceeded max attempts
          if (pollAttempts < maxPollAttempts) {
            setTimeout(pollForCompletion, 10000);
          } else {
            const errorMsg = error instanceof Error ? error.message : 'OAuth authentication failed';
            toast({
              title: 'Authentication Failed',
              description: errorMsg,
              variant: 'destructive'
            });
            onError(errorMsg);
            setConnecting(false);
            
            // Close window on error
            try {
              authWindow?.close();
            } catch (e) {
              // Ignore
            }
          }
        }
      };
      
      // Start polling after 5 seconds to give user time to see the popup
      setTimeout(pollForCompletion, 5000);

    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to initiate OAuth flow';
      onError(errorMsg);
      setConnecting(false);
    }
  };

  return { handleOAuth };
};